/**
 * Curated presets for styles, sources, glyphs, and sprites.
 * All are free for commercial use with attribution.
 */

export interface StylePreset {
  name: string;
  url: string;
  description: string;
  coverage: string;
  attribution: string;
}

export interface SourcePreset {
  name: string;
  type: "vector" | "raster" | "raster-dem";
  config: Record<string, unknown>;
  description: string;
  attribution: string;
}

export interface GlyphPreset {
  name: string;
  url: string;
  fonts: string[];
  description: string;
}

export interface SpritePreset {
  name: string;
  url: string;
  description: string;
}

// ── Full Style Presets ──

export const STYLE_PRESETS: StylePreset[] = [
  {
    name: "OpenFreeMap Liberty",
    url: "https://tiles.openfreemap.org/styles/liberty",
    description: "Full OSM style with 3D buildings, roads, labels, and sprites",
    coverage: "Global",
    attribution: "© OpenStreetMap contributors",
  },
  {
    name: "OpenFreeMap Positron",
    url: "https://tiles.openfreemap.org/styles/positron",
    description: "Light, minimal style for data overlays",
    coverage: "Global",
    attribution: "© OpenStreetMap contributors",
  },
  {
    name: "OpenFreeMap Bright",
    url: "https://tiles.openfreemap.org/styles/bright",
    description: "Colorful OSM-based street map",
    coverage: "Global",
    attribution: "© OpenStreetMap contributors",
  },
  {
    name: "swisstopo Base Map",
    url: "https://vectortiles.geo.admin.ch/styles/ch.swisstopo.basemap.vt/style.json",
    description: "Official Swiss topographic map with terrain",
    coverage: "Switzerland",
    attribution: "© swisstopo",
  },
  {
    name: "swisstopo Light",
    url: "https://vectortiles.geo.admin.ch/styles/ch.swisstopo.lightbasemap.vt/style.json",
    description: "Simplified Swiss base map",
    coverage: "Switzerland",
    attribution: "© swisstopo",
  },
  {
    name: "swisstopo Winter",
    url: "https://vectortiles.geo.admin.ch/styles/ch.swisstopo.basemap-winter.vt/style.json",
    description: "Swiss map with winter/snow styling",
    coverage: "Switzerland",
    attribution: "© swisstopo",
  },
  {
    name: "CARTO Positron",
    url: "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json",
    description: "Clean light basemap",
    coverage: "Global",
    attribution: "© CARTO, © OpenStreetMap contributors",
  },
  {
    name: "CARTO Dark Matter",
    url: "https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json",
    description: "Dark basemap for data visualization",
    coverage: "Global",
    attribution: "© CARTO, © OpenStreetMap contributors",
  },
  {
    name: "CARTO Voyager",
    url: "https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json",
    description: "Detailed street-level map",
    coverage: "Global",
    attribution: "© CARTO, © OpenStreetMap contributors",
  },
  {
    name: "Versatiles Colorful",
    url: "https://tiles.versatiles.org/assets/styles/colorful.json",
    description: "Colorful FLOSS map stack",
    coverage: "Global",
    attribution: "© OpenStreetMap contributors, © Versatiles",
  },
  {
    name: "MapLibre Demo",
    url: "https://demotiles.maplibre.org/style.json",
    description: "Lightweight demo tiles for testing",
    coverage: "Global",
    attribution: "© MapLibre, © OpenStreetMap contributors",
  },
];

// ── Source Presets (add to existing style) ──

export const SOURCE_PRESETS: SourcePreset[] = [
  {
    name: "OpenFreeMap Tiles",
    type: "vector",
    config: { type: "vector", url: "https://tiles.openfreemap.org/planet" },
    description: "Global OSM vector tiles",
    attribution: "© OpenStreetMap contributors",
  },
  {
    name: "swisstopo Vector",
    type: "vector",
    config: { type: "vector", url: "https://vectortiles.geo.admin.ch/tiles/ch.swisstopo.basemap.vt/v1.0.0/tiles.json" },
    description: "Swiss topographic vector tiles",
    attribution: "© swisstopo",
  },
  {
    name: "swisstopo Relief",
    type: "vector",
    config: { type: "vector", url: "https://vectortiles.geo.admin.ch/tiles/ch.swisstopo.relief.vt/v1.0.0/tiles.json" },
    description: "Swiss terrain relief",
    attribution: "© swisstopo",
  },
  {
    name: "MapLibre Demo Tiles",
    type: "vector",
    config: { type: "vector", url: "https://demotiles.maplibre.org/tiles/tiles.json" },
    description: "Lightweight demo vector tiles",
    attribution: "© MapLibre",
  },
];

// ── Glyph Presets ──

export const GLYPH_PRESETS: GlyphPreset[] = [
  {
    name: "OpenFreeMap Fonts",
    url: "https://tiles.openfreemap.org/fonts/{fontstack}/{range}.pbf",
    fonts: ["Open Sans Regular", "Open Sans Bold", "Open Sans Semibold", "Open Sans Italic"],
    description: "Standard web fonts via OpenFreeMap CDN",
  },
  {
    name: "MapLibre Demo Fonts",
    url: "https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf",
    fonts: ["Open Sans Regular", "Open Sans Bold"],
    description: "Basic fonts from MapLibre demo server",
  },
  {
    name: "MapTiler Fonts",
    url: "https://fonts.openmaptiles.org/{fontstack}/{range}.pbf",
    fonts: [
      "Open Sans Regular", "Open Sans Bold", "Open Sans Semibold", "Open Sans Italic",
      "Noto Sans Regular", "Noto Sans Bold", "Noto Sans Italic",
      "Klokantech Noto Sans Regular", "Klokantech Noto Sans Bold",
    ],
    description: "Extended font collection including Noto Sans for CJK support",
  },
  {
    name: "swisstopo Fonts",
    url: "https://vectortiles.geo.admin.ch/fonts/{fontstack}/{range}.pbf",
    fonts: ["Frutiger Neue Regular", "Frutiger Neue Bold", "Frutiger Neue Italic"],
    description: "Swiss official Frutiger fonts",
  },
  {
    name: "Versatiles Fonts",
    url: "https://tiles.versatiles.org/assets/fonts/{fontstack}/{range}.pbf",
    fonts: ["Open Sans Regular", "Open Sans Bold"],
    description: "Fonts from Versatiles CDN",
  },
];

// ── Sprite Presets ──

export const SPRITE_PRESETS: SpritePreset[] = [
  {
    name: "OpenFreeMap Liberty",
    url: "https://tiles.openfreemap.org/sprites/ofm_f384/ofm",
    description: "Icons for the Liberty style (POIs, road shields, transit)",
  },
  {
    name: "OSM Liberty",
    url: "https://maputnik.github.io/osm-liberty/sprites/osm-liberty",
    description: "Icons for OSM Liberty style",
  },
  {
    name: "swisstopo",
    url: "https://vectortiles.geo.admin.ch/styles/ch.swisstopo.basemap.vt/sprite/sprite",
    description: "Swiss topographic map symbols",
  },
  {
    name: "MapLibre Demo",
    url: "https://demotiles.maplibre.org/styles/osm-bright-gl-style/sprite",
    description: "Basic sprite set from MapLibre demo",
  },
];
